import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import useAxios from "axios-hooks";
import AutocompleteInput from "../../components/input/AutoCompleteInput";
import PaginationButtons from "../../components/pagination/PaginationButtons";
import TransferList from "./TransferList";

interface AssignmentContentManagerProps {
  type: "courses" | "labs"; // extend this if needed
  label?: string;
  contentKey: string; // key in `assignment`, like `courses`, `labs`
  listUrl?: string; // override for GET list if needed
}

export default function AssignmentContentManager({
  type,
  label,
  contentKey,
  listUrl,
}: AssignmentContentManagerProps) {
  const context = useOutletContext() as any;
  const params = useParams();

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const [{ data, loading }, getItems] = useAxios<any>({
    url: listUrl ?? `/assignments/${params.assignmentId}/${type}`,
    params: { page, limit: pageSize, search },
  });

  const [, executeAdd] = useAxios(
    {
      method: "POST",
      url: `/assignments/${params.assignmentId}/${type}/add`,
    },
    { manual: true }
  );

  const [, executeRemove] = useAxios(
    {
      method: "POST",
      url: `/assignments/${params.assignmentId}/${type}/remove`,
    },
    { manual: true }
  );

  useEffect(() => {
    if (search !== undefined) {
      getItems();
    }
  }, [search]);

  const contentIds = context.assignment?.[contentKey] ?? [];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={setSearch}
            placeHolder={`Search ${label || type}...`}
            className="bg-base-200"
          />
        </div>
        <div className="ml-auto">
          <button
            className="btn btn-sm ml-4"
            onClick={() => {
              setShowModal(true);
              (document?.getElementById("select_modal") as any)?.showModal();
            }}
          >
            Add {label || type}
          </button>
          {selectedRows.length > 0 && (
            <button
              className="btn btn-sm ml-4 btn-outline btn-error"
              onClick={async () => {
                try {
                  await executeRemove({ data: { content: selectedRows } });
                  setSelectedRows([]);
                  setSelectAll(false);
                  getItems();
                } catch (error) {
                  console.error(`Failed to remove ${type}`, error);
                }
              }}
            >
              Remove {selectedRows.length} {label || type}
            </button>
          )}
        </div>
      </div>

      <dialog id="select_modal" className="modal">
        <div className="bg-base-100 p-10 rounded-2xl w-[90vw] h-[90vh]">
          <div className="h-full">
            {showModal && (
              <TransferList
                isOpen={showModal}
                onFinish={async (ids: string[]) => {
                  try {
                    await executeAdd({ data: { content: ids } });
                    setShowModal(false);
                    (document?.getElementById("select_modal") as any)?.close();
                    getItems();
                  } catch (error) {
                    console.error(`Failed to add ${type}`, error);
                  }
                }}
                currentContentIds={contentIds}
                url={`/${type}`}
              />
            )}
          </div>
        </div>
      </dialog>

      {!loading && (
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100 table-container">
            <table className="table table-md w-full">
              <thead className="bg-base-200 sticky top-0 z-10">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectAll}
                      onChange={() => {
                        const select = !selectAll;
                        setSelectAll(select);
                        setSelectedRows(
                          select ? data.docs.map((d: any) => d._id) : []
                        );
                      }}
                    />
                  </th>
                  <th className="w-[20%]">Name</th>
                  <th>Description</th>
                  <th className="text-center">Difficulty</th>
                </tr>
              </thead>
              <tbody>
                {data?.docs?.map((item: any) => (
                  <tr key={item._id} className="hover:bg-base-300 transition">
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={selectedRows.includes(item._id)}
                        onChange={() => {
                          const updated = selectedRows.includes(item._id)
                            ? selectedRows.filter((id) => id !== item._id)
                            : [...selectedRows, item._id];
                          setSelectedRows(updated);
                        }}
                      />
                    </td>
                    <td className="font-medium">{item.name}</td>
                    <td>{item?.info?.description?.about}</td>
                    <td className="text-center">{item?.info?.difficulty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      )}
    </div>
  );
}
